import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { Avatar } from '@/components/ui';
import { IconCheck, IconChevronDown, IconSearch } from '@/components/Icons';
import { fold, searchWords } from '@/lib/linkSearch';
import type { User } from '@/lib/types';

interface Option {
  /** Vazio: os links de quem está vendo. */
  id: string;
  name: string;
  detail: string;
  person: Pick<User, 'name' | 'avatarColor' | 'photoUrl'> | null;
}

interface Props {
  /** Quem está vendo: a primeira opção, "Os meus links". */
  self: User | null;
  /** Os outros membros. */
  members: readonly User[];
  /** O membro em vista; vazio = os links de quem está vendo. */
  value: string;
  onChange: (memberId: string) => void;
}

/**
 * O membro cujos links aparecem, achado por busca: parte do nome ou do usuário.
 * A lista abre sobre a página ao entrar no campo; Enter escolhe, Esc desiste e
 * o campo volta a dizer quem está em vista.
 */
export function MemberPicker({ self, members, value, onChange }: Props) {
  const id = useId();
  const inputId = `${id}-campo`;
  const listId = `${id}-lista`;
  const options = useMemo<Option[]>(
    () => [
      { id: '', name: 'Os meus links', detail: self ? `@${self.login}` : '', person: self },
      ...members.map((member) => ({
        id: member.id,
        name: member.name,
        detail: member.active ? `@${member.login}` : `@${member.login} · inativo`,
        person: member,
      })),
    ],
    [self, members],
  );
  const current = options.find((option) => option.id === value) ?? options[0]!;
  const [open, setOpen] = useState(false);
  /** O que foi digitado; nulo enquanto o campo mostra o membro em vista. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  const words = searchWords(query ?? '');
  const shown = words.length === 0 ? options : options.filter((option) => {
    const text = fold(`${option.name} ${option.detail}`);
    return words.every((word) => text.includes(word));
  });

  useEffect(() => {
    if (open) document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, listId]);

  function show() {
    setOpen(true);
    setActive(Math.max(0, options.indexOf(current)));
  }

  function close() {
    setOpen(false);
    setQuery(null);
  }

  function choose(option: Option) {
    onChange(option.id);
    close();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) return show();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((index) => (shown.length === 0 ? 0 : (index + step + shown.length) % shown.length));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = shown[active];
      if (option) choose(option);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  }

  return (
    <div className="field member-picker">
      <label className="field-label" htmlFor={inputId}>Membro</label>
      <div className="search-input member-picker-control">
        <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
        <input
          id={inputId}
          className="inp"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder="Nome ou usuário"
          value={query ?? current.name}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={(event) => {
            event.target.select();
            show();
          }}
          onClick={() => (open ? undefined : show())}
          onBlur={close}
          onKeyDown={onKeyDown}
        />
        <span className="member-picker-chevron" aria-hidden="true"><IconChevronDown /></span>
      </div>

      {open ? (
        <ul id={listId} role="listbox" className="lead-picker-results member-picker-list" aria-label="Membros">
          {shown.map((option, index) => (
            <li
              key={option.id || 'eu'}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className="lead-picker-option member-picker-option"
              // mousedown, e não click: o clique viria depois do blur, que já fechou a lista.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(option);
              }}
              onMouseEnter={() => setActive(index)}
            >
              {option.person ? <Avatar name={option.person.name} color={option.person.avatarColor} photo={option.person.photoUrl} size={24} /> : null}
              <span className="member-picker-text">
                <span className="truncate" style={{ fontWeight: 500 }}>{option.name}</span>
                {option.detail ? <span className="lead-picker-sub truncate">{option.detail}</span> : null}
              </span>
              {option.id === current.id ? (
                <>
                  <IconCheck size={15} className="member-picker-current" />
                  <span className="sr-only"> (em vista)</span>
                </>
              ) : null}
            </li>
          ))}
          {shown.length === 0 ? (
            <li className="lead-picker-empty" role="presentation">Nenhum membro com “{query?.trim()}”.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
